import { useLocale } from "@/locale/LocaleContext";
import ui from "./ui.module.css";

type Step = { title?: unknown; detail?: unknown };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function TaskResultView({
  result,
  userInstructions,
}: {
  result: Record<string, unknown> | null;
  userInstructions: string;
}) {
  const { m, fmt } = useLocale();
  if (!result) {
    return <p className={ui.meta}>{m.tasks.noResult}</p>;
  }

  const engine = typeof result.engine === "string" ? result.engine : "";

  if (engine === "built-in-checklist") {
    const steps = Array.isArray(result.steps) ? (result.steps as Step[]) : [];
    return (
      <div className={ui.resultBlock}>
        <p className={ui.meta}>{m.checklist.engineNote}</p>
        <p style={{ margin: "0.35rem 0 1rem" }}>{m.checklist.summary}</p>
        <ol className={ui.checklist}>
          {m.checklistSteps.map((tpl, index) => {
            const apiDetail = steps[index]?.detail;
            const detail =
              index === 0
                ? userInstructions || (typeof apiDetail === "string" ? apiDetail : tpl.detail)
                : tpl.detail;
            return (
              <li key={tpl.title}>
                <strong>
                  {fmt(m.checklist.stepLabel, { n: index + 1 })} · {tpl.title}
                </strong>
                <p>{detail}</p>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  if (engine === "mac-readonly-diagnostics") {
    const known: Array<[string, string]> = [
      ["cpu", m.checklist.fieldCpu],
      ["memory", m.checklist.fieldMem],
      ["mem", m.checklist.fieldMem],
      ["disk", m.checklist.fieldDisk],
      ["uptime", m.checklist.fieldUptime],
    ];
    const shown = new Set<string>(["engine", "ai_generated"]);
    return (
      <div className={ui.resultBlock}>
        <p className={ui.meta}>{m.checklist.diagnosticsNote}</p>
        <h3 className={ui.resultHeading}>{m.checklist.diagnosticsHeading}</h3>
        <dl className={ui.kv}>
          {known.map(([key, label]) => {
            if (result[key] == null) return null;
            shown.add(key);
            return (
              <div key={key}>
                <dt>{label}</dt>
                <dd className="mono">{String(result[key])}</dd>
              </div>
            );
          })}
        </dl>
        {Object.keys(result).some((k) => !shown.has(k)) ? (
          <>
            <p className={ui.meta}>{m.checklist.otherFields}</p>
            <dl className={ui.kv}>
              {Object.entries(result).map(([key, value]) => {
                if (shown.has(key)) return null;
                if (value && typeof value === "object") return null;
                return (
                  <div key={key}>
                    <dt>{key}</dt>
                    <dd className="mono">{String(value)}</dd>
                  </div>
                );
              })}
            </dl>
          </>
        ) : null}
      </div>
    );
  }

  // Unknown structured result: key/value, not raw JSON dump.
  const record = asRecord(result) ?? {};
  return (
    <dl className={ui.kv}>
      {Object.entries(record).map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd className="mono">
            {value && typeof value === "object" ? JSON.stringify(value) : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}
